import { NotFound } from '@pages/NotFound';
import { Profile } from '@pages/profile/Profile';
import Template from '@pages/Template/Template';
import { Login } from '@pages/auth/Login';
import DashBoard from '@pages/dashboard/Dashboard';
import { ResetPassword } from '@pages/auth/ResetPassword';
import { createBrowserRouter } from 'react-router-dom';
import { ForgotPassword } from '@pages/auth/ForgotPassword';
import { SetPassword } from '@pages/auth/SetPassword';
import { Users } from '@pages/users/Users';
import InviteCreate from '@pages/users/InviteCreate';
import UserUpdate from '@pages/users/UserUpdate';
import { ProtectedRoute } from './components/ProtectedRoute';
import Clients from '@pages/clients/Clients';
import { ClientCreate } from '@pages/clients/ClientCreate';
import { UpdateClientContact } from '@pages/clients/UpdateClientContact';
import { ClientUpdate } from '@pages/clients/ClientUpdate';
import { useUserStore } from './stores/user';
import BillingPreferencesUpdate from '@pages/clients/BillingPreferencesUpdate';
import PEMaintenance from '@pages/caseManagement/PEMaintenance';
import { PECreate } from '@pages/caseManagement/PECreate';
import { Courts } from '@pages/courts/Courts';
import CourtCreate from '@pages/courts/CourtCreate';
import CourtUpdate from '@pages/courts/CourtUpdate';
import { PEUpdate } from '@pages/caseManagement/PEUpdate';
import { Vendors } from './pages/vendors/Vendors';
import { VendorCreate } from '@pages/vendors/VendorCreate';
import { VendorUpdate } from '@pages/vendors/VendorUpdate';
import { Feature } from './utils/constant';
import { WorkOrderAttempts } from '@pages/workOrderAttempts/WorkOrderAttempts';
import { ProcessBillingServerFormPage } from '@pages/caseManagement/processBilling/ProcessBillingServerFormPage';
import { ProcessBillingServerView } from '@pages/caseManagement/processBilling/ProcessBillingServerView';
import { ProcessBillingVendorFormPage } from '@pages/caseManagement/processBilling/ProcessBillingVendorFormPage';
import { ProcessBillingVendorView } from '@pages/caseManagement/processBilling/ProcessBillingVendorView';
import { InvoiceView } from '@pages/caseManagement/invoice/InvoiceView';
import AccountsReceivable from '@pages/accounts/AccountsReceivables';
import { DeliveryCreate } from '@pages/delivery/DeliveryCreate';
import DeliveryList from '@pages/delivery/DeliveryList';
import { DeliveryUpdateManage } from '@pages/delivery/DeliveryUpdateManage';
import { DeliveryInvoiceView } from '@pages/delivery/invoice/DeliveryInvoiceView';
import CheckRegister from '@pages/accounts/CheckRegister';
import Statements from '@pages/accounts/Statements';
import VendorPayment from '@pages/accounts/VendorPayment';
import PaymentHistory from '@pages/accounts/PaymentHistory';
import Recaps from '@pages/recaps/Recaps';
import FinalBillingSummary from '@pages/fbs/FinalBillingSummary';
import HIList from '@pages/homeInspection/HIList';
import { HICreate } from '@pages/homeInspection/HICreate';
import { HIEdit } from '@pages/homeInspection/HIEdit';
import { HiAttemptCreate } from '@pages/homeInspection/HiAttemptCreate';
import { HiAttemptEdit } from '@pages/homeInspection/HiAttemptEdit';
import { HiAttemptView } from '@pages/homeInspection/HiAttemptView';
import { HiInvoiceView } from '@pages/homeInspection/invoice/HiInvoiceView';
import DDIList from '@pages/dueDiligence/DDIList';
import { DDIWorkOrderCreate } from '@pages/dueDiligence/DDIWorkOrderCreate';
import { DDIWorkOrderEdit } from '@pages/dueDiligence/DDIWorkOrderEdit';
import { DDISearchResultCreate } from '@pages/dueDiligence/DDISearchResultCreate';
import { DDISearchResultView } from '@pages/dueDiligence/DDISearchResultView';
import { DDISearchResultEdit } from '@pages/dueDiligence/DDISearchResultEdit';
import { DdiInvoiceView } from '@pages/dueDiligence/invoice/DdiInvoiceView';

const userStore = useUserStore.getState();

const Routes = createBrowserRouter([
  {
    path: '/',
    element: <Template />,
    children: [
      {
        path: 'dashboard',
        element: (
          <ProtectedRoute
            element={<DashBoard />}
            roles={['ADMIN', 'ACCOUNTANT', 'SERVER', 'OPERATOR']}
            requiredFeature={Feature.DASHBOARD}
          />
        ),
        index: true,
      },
      {
        path: 'accounts',
        children: [
          {
            path: 'receivable',
            element: (
              <ProtectedRoute
                element={<AccountsReceivable />}
                roles={['ADMIN', 'ACCOUNTANT']}
                teamKeys={['ACCOUNTING']}
                requiredFeature={Feature.ACCOUNTS_RECEIVABLE}
              />
            ),
          },
          {
            path: 'check-register',
            element: (
              <ProtectedRoute
                element={<CheckRegister />}
                roles={['ADMIN', 'ACCOUNTANT']}
                teamKeys={['ACCOUNTING']}
                requiredFeature={Feature.CHECK_REGISTER}
              />
            ),
          },
          {
            path: 'statements',
            element: (
              <ProtectedRoute
                element={<Statements />}
                roles={['ADMIN', 'ACCOUNTANT']}
                teamKeys={['ACCOUNTING']}
                requiredFeature={Feature.STATEMENTS}
                allowSupervisor
              />
            ),
          },
          {
            path: 'vendor-payment',
            element: (
              <ProtectedRoute
                element={<VendorPayment />}
                roles={['ADMIN', 'ACCOUNTANT']}
                teamKeys={['ACCOUNTING']}
                requiredFeature={Feature.VENDOR_PAYMENT}
              />
            ),
          },
          {
            path: 'payment-history',
            element: (
              <ProtectedRoute
                element={<PaymentHistory />}
                roles={['ADMIN', 'ACCOUNTANT']}
                teamKeys={['ACCOUNTING']}
                requiredFeature={Feature.PAYMENT_HISTORY}
              />
            ),
          },
        ],
      },

      {
        path: 'users',
        children: [
          {
            path: '',
            element: <ProtectedRoute element={<Users />} roles={['ADMIN']} requiredFeature={Feature.USERS} />,
          },
          {
            path: 'update/:userId',
            element: <ProtectedRoute element={<UserUpdate />} roles={['ADMIN']} requiredFeature={Feature.USERS} />,
          },
          {
            path: 'invites/add',
            element: <ProtectedRoute element={<InviteCreate />} roles={['ADMIN']} requiredFeature={Feature.USERS} />,
          },
          {
            path: 'list',
            element: <ProtectedRoute element={<InviteCreate />} roles={['ADMIN']} requiredFeature={Feature.USERS} />,
          },
        ],
      },
      {
        path: 'profile',
        element: <Profile />,
      },

      {
        path: 'process',
        children: [
          {
            path: '',
            element: <PEMaintenance />,
          },
          {
            path: 'create',
            element: <PECreate />,
          },
          {
            path: ':id/update',
            element: <PEUpdate />,
          },
          {
            path: 'workorder',

            children: [
              {
                path: ':id/attempts',
                element: <WorkOrderAttempts />,
              },
              {
                path: ':workOrderId/process-billing',
                element: (
                  <ProtectedRoute element={<ProcessBillingServerView />} roles={['ADMIN', 'BILLING', 'ACCOUNTANT']} teamKeys={['BILLING']} allowSupervisor />
                ),
              },
              {
                path: ':workOrderId/process-billing/create',
                element: (
                  <ProtectedRoute element={<ProcessBillingServerFormPage />} roles={['ADMIN', 'BILLING', 'ACCOUNTANT']} teamKeys={['BILLING']} allowSupervisor />
                ),
              },
              {
                path: ':workOrderId/process-billing/edit',
                element: (
                  <ProtectedRoute element={<ProcessBillingServerFormPage />} roles={['ADMIN']} allowSupervisor />
                ),
              },
              {
                path: ':workOrderId/process-billing-vendor',
                element: (
                  <ProtectedRoute element={<ProcessBillingVendorView />} roles={['ADMIN', 'BILLING', 'ACCOUNTANT']} teamKeys={['BILLING']} allowSupervisor />
                ),
              },
              {
                path: ':workOrderId/process-billing-vendor/create',
                element: (
                  <ProtectedRoute element={<ProcessBillingVendorFormPage />} roles={['ADMIN', 'BILLING', 'ACCOUNTANT']} teamKeys={['BILLING']} allowSupervisor />
                ),
              },
              {
                path: ':workOrderId/process-billing-vendor/edit',
                element: (
                  <ProtectedRoute element={<ProcessBillingVendorFormPage />} roles={['ADMIN']} allowSupervisor />
                ),
              },
            ],
          },
          {
            path: 'invoice',
            children: [
              {
                path: ':id',
                element: <InvoiceView mode="view" />,
              },
              {
                path: ':id/update',
                element: <InvoiceView mode="edit" />,
              },
            ],
          },
        ],
      },

      {
        path: 'courts',
        children: [
          {
            path: '',
            element: <Courts />,
          },
          {
            path: 'create',
            element: <CourtCreate />,
          },
          {
            path: 'update/:id',
            element: <CourtUpdate />,
          },
        ],
      },
      {
        path: 'vendors',
        children: [
          {
            path: '',
            element: <Vendors />,
          },
          {
            path: 'create',
            element: <VendorCreate />,
          },
          {
            path: 'update/:vendorId',
            element: <VendorUpdate />,
          },
        ],
      },
      {
        path: 'recaps',
        children: [
          {
            path: '',
            element: (
              <ProtectedRoute
                element={<Recaps />}
                roles={['ADMIN', 'ACCOUNTANT', 'BILLING']}
                teamKeys={['BILLING', 'ACCOUNTING']}
                requiredFeature={Feature.RECAPS}
                allowSupervisor
              />
            ),
          },
        ],
      },
      {
        path: 'final-billing-summary',
        children: [
          {
            path: '',
            element: (
              <ProtectedRoute
                element={<FinalBillingSummary />}
                roles={['ADMIN', 'ACCOUNTANT', 'BILLING']}
                teamKeys={['BILLING', 'ACCOUNTING']}
                requiredFeature={Feature.FBS}
                allowBillingSupervisor
              />
            ),
          },
        ],
      },
      {
        path: 'clients',
        children: [
          {
            path: '',
            element: <Clients />,
          },
          {
            path: 'create',
            element: <ClientCreate />,
          },
          {
            path: ':clientId/contacts',
            element: <UpdateClientContact />,
          },
          {
            path: 'update/:clientId',
            element: <ClientUpdate />,
          },
          {
            path: 'update/:clientId/billing-preference',
            element: (
              <ProtectedRoute
                element={<BillingPreferencesUpdate />}
                roles={['ADMIN', 'BILLING', 'OPERATOR']}
                requiredFeature={Feature.CLIENT_BILLING_PREFERENCES}
              />
            ),
          },
        ],
      },
      {
        path: 'delivery',
        children: [
          {
            path: '',
            element: (
              <ProtectedRoute
                element={<DeliveryList />}
                roles={['ADMIN', 'OPERATOR']}
                requiredFeature={Feature.DELIVERY}
              />
            ),
          },
          {
            path: 'create',
            element: (
              <ProtectedRoute
                element={<DeliveryCreate />}
                roles={['ADMIN', 'OPERATOR']}
                requiredFeature={Feature.DELIVERY}
              />
            ),
          },
          {
            path: ':intakeId/edit',
            element: (
              <ProtectedRoute
                element={<DeliveryCreate />}
                roles={['ADMIN', 'OPERATOR']}
                requiredFeature={Feature.DELIVERY}
              />
            ),
          },
          {
            path: 'work-order/:workOrderId/edit',
            element: (
              <ProtectedRoute
                element={<DeliveryCreate />}
                roles={['ADMIN', 'OPERATOR', 'SERVER']}
                requiredFeature={Feature.DELIVERY}
              />
            ),
          },
          {
            // Add mode. Fine-grained access (canEditDeliveryUpdates) is enforced in DeliveryUpdateManage.
            path: 'work-order/:workOrderId/delivery-updates',
            element: (
              <ProtectedRoute
                element={<DeliveryUpdateManage />}
                roles={['ADMIN', 'OPERATOR', 'SERVER', 'ACCOUNTANT']}
                requiredFeature={Feature.DELIVERY_UPDATES}
              />
            ),
          },
          {
            // View/Edit mode. Read vs edit access is enforced in DeliveryUpdateManage.
            path: 'work-order/:workOrderId/delivery-update/:updateId',
            element: (
              <ProtectedRoute
                element={<DeliveryUpdateManage />}
                roles={['ADMIN', 'OPERATOR', 'SERVER', 'ACCOUNTANT']}
                requiredFeature={Feature.DELIVERY_UPDATES}
              />
            ),
          },
          {
            path: 'invoice/:invoiceNumber',
            element: (
              <ProtectedRoute
                element={<DeliveryInvoiceView mode="view" />}
                roles={['ADMIN', 'OPERATOR', 'SERVER', 'ACCOUNTANT']}
                requiredFeature={Feature.DELIVERY}
              />
            ),
          },
          {
            path: 'invoice/:invoiceNumber/edit',
            element: (
              <ProtectedRoute
                element={<DeliveryInvoiceView mode="edit" />}
                roles={['ADMIN', 'OPERATOR']}
                requiredFeature={Feature.DELIVERY}
              />
            ),
          },
        ],
      },
      {
        path: 'home-inspection',
        children: [
          {
            path: '',
            element: (
              <ProtectedRoute
                element={<HIList />}
                roles={['ADMIN', 'ACCOUNTANT', 'SERVER', 'OPERATOR']}
                requiredFeature={Feature.HOME_INSPECTION}
              />
            ),
          },
          {
            path: 'create',
            element: (
              <ProtectedRoute
                element={<HICreate />}
                roles={['ADMIN', 'OPERATOR']}
                requiredFeature={Feature.HOME_INSPECTION_WORK_ORDER_CREATE}
              />
            ),
          },
          {
            path: ':id/update',
            element: (
              <ProtectedRoute
                element={<HIEdit />}
                roles={['ADMIN', 'OPERATOR']}
                requiredFeature={Feature.HOME_INSPECTION_WORK_ORDER_UPDATE}
              />
            ),
          },
          {
            path: ':id/attempt/create',
            element: (
              <ProtectedRoute
                element={<HiAttemptCreate />}
                roles={['ADMIN', 'OPERATOR', 'SERVER']}
              />
            ),
          },
          {
            path: ':id/attempt/:attemptId/edit',
            element: (
              <ProtectedRoute
                element={<HiAttemptEdit />}
                roles={['ADMIN', 'OPERATOR', 'SERVER']}
              />
            ),
          },
          {
            path: ':id/attempt/:attemptId/view',
            element: (
              <ProtectedRoute
                element={<HiAttemptView />}
                roles={['ADMIN', 'SERVER', 'OPERATOR']}
              />
            ),
          },
          {
            // Issue #458: was Process Service InvoiceView (wrong API) — use HI invoice page
            path: 'invoice/:id',
            element: (
              <ProtectedRoute
                element={<HiInvoiceView mode="view" />}
                roles={['ADMIN', 'ACCOUNTANT', 'SERVER', 'OPERATOR']}
                requiredFeature={Feature.HOME_INSPECTION}
              />
            ),
          },
          {
            path: 'invoice/:id/update',
            element: (
              <ProtectedRoute
                element={<HiInvoiceView mode="edit" />}
                roles={['ADMIN', 'OPERATOR']}
                allowSupervisor
                requiredFeature={Feature.HOME_INSPECTION}
              />
            ),
          },
        ],
      },
      {
        path: 'due-diligence',
        children: [
          {
            path: '',
            element: (
              <ProtectedRoute
                element={<DDIList />}
                roles={['ADMIN', 'ACCOUNTANT', 'SERVER', 'OPERATOR']}
                requiredFeature={Feature.DUE_DILIGENCE_WORK_ORDER_READ}
              />
            ),
          },
          {
            path: 'create',
            element: (
              <ProtectedRoute
                element={<DDIWorkOrderCreate />}
                roles={['ADMIN', 'ACCOUNTANT', 'SERVER', 'OPERATOR']}
                teamKeys={['DUE_DILIGENCE']}
                allowSupervisor
                requiredFeature={Feature.DUE_DILIGENCE_WORK_ORDER_CREATE}
              />
            ),
          },
          {
            path: ':id/update',
            element: (
              <ProtectedRoute
                element={<DDIWorkOrderEdit />}
                roles={['ADMIN', 'ACCOUNTANT', 'SERVER', 'OPERATOR']}
                teamKeys={['DUE_DILIGENCE']}
                allowSupervisor
                requiredFeature={Feature.DUE_DILIGENCE_WORK_ORDER_EDIT}
              />
            ),
          },
          {
            path: ':id/search-results/create',
            element: (
              <ProtectedRoute
                element={<DDISearchResultCreate />}
                roles={['ADMIN', 'ACCOUNTANT', 'SERVER', 'OPERATOR']}
                requiredFeature={Feature.DUE_DILIGENCE_WORK_ORDER_READ}
                />
            ),
          },
          {
            path: 'invoice/:invoiceNumber',
            element: (
              <ProtectedRoute
                element={<DdiInvoiceView mode="view" />}
                roles={['ADMIN', 'ACCOUNTANT', 'SERVER', 'OPERATOR']}
                requiredFeature={Feature.DUE_DILIGENCE_INVOICE}
              />
            ),
          },
          {
            path: ':id/search-results/view',
            element: (
              <ProtectedRoute
                element={<DDISearchResultView />}
                roles={['ADMIN', 'ACCOUNTANT', 'SERVER', 'OPERATOR']}
                requiredFeature={Feature.DUE_DILIGENCE_WORK_ORDER_READ}
              />
            ),
          },
          {
            path: ':id/search-results/edit',
            element: (
              <ProtectedRoute
                element={<DDISearchResultEdit />}
                roles={['ADMIN', 'ACCOUNTANT', 'SERVER', 'OPERATOR']}
                requiredFeature={Feature.DUE_DILIGENCE_WORK_ORDER_READ}
                />
            ),
          },
          {
            path: 'invoice/:invoiceNumber/update',
            element: (
              <ProtectedRoute
                element={<DdiInvoiceView mode="edit" />}
                roles={['ADMIN', 'ACCOUNTANT', 'SERVER', 'OPERATOR']}
                requiredFeature={Feature.DUE_DILIGENCE_INVOICE}
              />
            ),
          },
        ],
      },
      ...(userStore.accessToken
        ? [
            {
              path: '*',
              element: <NotFound mode="LOGGEDIN" />,
            },
          ]
        : []),
    ],
  },
  {
    path: 'reset-password/:payload',
    element: <ResetPassword type="RESET_PASSWORD" />,
  },
  {
    path: 'forgot-password/:payload',
    element: <ResetPassword type="FORGOT_PASSWORD" />,
  },
  {
    path: 'login',
    element: <Login />,
  },
  {
    path: 'forgot-password',
    element: <ForgotPassword />,
  },
  {
    path: 'set-password',
    element: <SetPassword />,
  },
  {
    path: '*',
    element: <NotFound mode="LOGGEDOUT" />,
  },
]);

export default Routes;
